import { useEffect, type Dispatch, type SetStateAction } from 'react'
import type { ChartManager } from '../core/ChartManager'
import { CANDLE_PANE_ID } from '../types/chart'
import { LAYOUT_CHANGED_EVENT } from '../core/EventBus'

type Bounding = { width: number; height: number; left?: number; top?: number }

/**
 * Tracks whether the chart is scrolled away from the latest bar and computes
 * the position for a "scroll to latest" button overlay.
 *
 * Sets scrollToLatestPos to null when at the latest bar, or
 * { bottom, right } pixel offsets when scrolled away.
 *
 * Two signals drive a recompute, both coalesced into one rAF read:
 *
 * - klinecharts `onVisibleRangeChange` (pan / zoom). klinecharts only fires
 *   this from a layout pass when the main-area WIDTH changes, so it is silent
 *   for height-only relayouts: dropping the VOL sub-pane, a separator drag, a
 *   grid-cell height change. The button then floated where the old pane edge
 *   used to be until the next pan.
 * - the manager's `layout:changed`, emitted after every relayout site, which
 *   covers those height-only cases.
 *
 * The geometry read is deferred to the next frame because klinecharts fires
 * the range event BEFORE writing the new y-axis bounding in the same pass; a
 * synchronous read saw the previous layout's axis width.
 */
export function useScrollTracking(
  managerRef: React.RefObject<ChartManager | null>,
  setScrollToLatestPos: Dispatch<SetStateAction<{ bottom: number; right: number } | null>>,
  /** Re-subscribes when this changes (the chart's datafeed). */
  resubscribeOn: unknown,
  /** Scales hidden: the y-axis is drawn `inside`, so there is no strip to clear. */
  hideAxisScale = false,
  /** Price scale on the left: nothing sits between the button and the right edge. */
  axisOnLeft = false,
): void {
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return

    const chart = manager.getChart()
    let rafId: number | null = null

    const read = () => {
      rafId = null
      const range = chart.getVisibleRange()
      const dataLen = chart.getDataList().length
      const isAway = dataLen > 0 && range.to < dataLen - 1
      if (!isAway) {
        setScrollToLatestPos(null)
        return
      }
      // Position at bottom-right of price pane, flush against the y-axis strip.
      const paneSize = chart.getSize(CANDLE_PANE_ID) as Bounding | null
      const yAxisSize = chart.getSize(CANDLE_PANE_ID, 'yAxis' as never) as Bounding | null
      const totalSize = chart.getSize() as Bounding | null
      if (paneSize && totalSize) {
        const paneBottom = (paneSize.top ?? 0) + paneSize.height
        const bottom = totalSize.height - paneBottom + 8
        const axisInset = hideAxisScale || axisOnLeft ? 0 : (yAxisSize?.width ?? 0)
        const right = axisInset + 4
        // Events fire per pan/zoom frame while scrolled away; keep the previous
        // object when the offsets are unchanged so the host doesn't re-render.
        setScrollToLatestPos((prev) =>
          prev && prev.bottom === bottom && prev.right === right ? prev : { bottom, right },
        )
      }
    }

    const schedule = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(read)
    }

    chart.subscribeAction('onVisibleRangeChange' as never, schedule)
    const offLayout = manager.getEventBus().on(LAYOUT_CHANGED_EVENT, schedule)
    // Seed on (re)subscribe: an axis-side or hide-scale flip re-runs this
    // effect, and the position must not wait for the next event to catch up.
    schedule()
    return () => {
      chart.unsubscribeAction('onVisibleRangeChange' as never, schedule)
      offLayout()
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [managerRef, setScrollToLatestPos, hideAxisScale, axisOnLeft, resubscribeOn])
}
